import { mergeSubsetOrder } from "./ordering";

interface OrderWrite {
  apply: (ids: string[], pending: boolean) => void;
  write: (ids: string[]) => Promise<unknown>;
  onError: (error: unknown) => void;
  onSettled: () => void;
}

interface OrderQueue {
  tail: Promise<unknown>;
  confirmed: Set<(ids: string[]) => void>;
}
const queues = new WeakMap<object, Map<string, OrderQueue>>();

/** A service/order scope owns the request queue; each mounted view owns its optimistic state. */
export function createOrderWriter(owner: object, scope: string) {
  let scopes = queues.get(owner);
  if (!scopes) queues.set(owner, scopes = new Map());
  let queue = scopes.get(scope);
  if (!queue) scopes.set(scope, queue = { tail: Promise.resolve(), confirmed: new Set() });
  const shared = queue;
  let pending = 0;
  let confirmed: string[] = [];
  const reflect = (ids: string[]) => {
    const known = new Set(confirmed);
    confirmed = mergeSubsetOrder(confirmed, ids.filter((id) => known.has(id)));
  };
  return (ids: string[], previous: string[], opts: OrderWrite) => {
    if (!pending) {
      confirmed = [...previous];
      shared.confirmed.add(reflect);
    }
    pending += 1;
    const next = [...ids];
    opts.apply(next, true);
    const result = shared.tail.then(async () => {
      try {
        await opts.write(next);
        shared.confirmed.forEach((reflect) => reflect(next));
      } catch (error) {
        opts.onError(error);
      } finally {
        pending -= 1;
        if (!pending) {
          shared.confirmed.delete(reflect);
          opts.apply([...confirmed], false);
          opts.onSettled();
        }
      }
    });
    shared.tail = result.catch(() => {});
    return result;
  };
}
